import {skipToken} from '@reduxjs/toolkit/query';
import {isFeatureEnabled} from 'feature-flags';
import {Option} from 'platform/components';
import {useLocale} from 'platform/locale';

import {isNil, isNotNil, last, length, omit} from 'ramda';

import {usePriceReport} from '../../../hooks/usePriceReport';
import {useDrillDownQuery} from '../../../store/omneticApi';
import {DrillDownLevelName} from '../../../types/api/drillDown';
import {featureFlags} from '../../../utils/featureFlags';
import {getPermanentLevelFilters} from '../../DrillDown/components/DrillDown/utils/getPermanentLevelFilters';

const useGetCounts = (
  optionKey: DrillDownLevelName,
  filters: Partial<Record<DrillDownLevelName, string>>
) => {
  const {requestData} = usePriceReport();
  const {language} = useLocale();
  const levelFilters = getPermanentLevelFilters(omit([optionKey], filters));
  const {data: drillDownData} = useDrillDownQuery(
    isNotNil(requestData?.market)
      ? {
          levels: [
            ...levelFilters,
            {
              level: optionKey,
              values: [],
              priority: length(levelFilters) + 1,
            },
          ],
          currency: 'CZK', // We don't know currency, but we are not going to use prices
          regions: [requestData.market],
          lang: language,
        }
      : skipToken,
    {
      skip:
        isNil(filters.make) ||
        isNil(filters.model) ||
        !isFeatureEnabled(featureFlags.PR_FORM_STATISTICS),
    }
  );

  return drillDownData?.statisticsAggregated.map((item) => ({
    option: last(item.levels)?.value,
    count: item.statistics.reduce((acc, curr) => acc + curr.amount, 0),
  }));
};

export const useEnrichOptionsWithCount = (
  options: Option[],
  optionKey: DrillDownLevelName,
  filters: Partial<Record<DrillDownLevelName, string>>
): Option[] => {
  const counts = useGetCounts(optionKey, filters);

  if (
    isNil(filters.make) ||
    isNil(filters.model) ||
    !isFeatureEnabled(featureFlags.PR_FORM_STATISTICS)
  ) {
    return options ?? [];
  }

  return (
    options.map((item) => {
      const count = counts?.find((count) => count.option === item.value)?.count;
      return {
        ...item,
        suffix: isNotNil(count) ? `(${count})` : undefined,
      };
    }) ?? []
  );
};

export const useGetPowerOptionsWithCount = (
  filters: Partial<Record<DrillDownLevelName, string>>
): Option[] => {
  const counts = useGetCounts('power', filters);

  return (
    counts
      ?.sort((a, b) => {
        const aValue = Number(a.option);
        const bValue = Number(b.option);
        return aValue - bValue;
      })
      ?.map((count) => ({
        value: count.option ?? '?',
        label: `${count.option} kW`,
        suffix: isNotNil(count.count) ? `(${count.count})` : undefined,
      })) ?? []
  );
};
